import { useCallback } from 'react';

import { useAppStore } from '@/store';
import { refreshMonetization, useCredits } from '@/features/credits';
import submitWebHook, {
  FreeQuotaExceededError,
  type FreeQuotaReason,
} from '@/requests/magic-flow/submitWebHook';
import createNotificationConfig from '@/utils/ui/createNotificationConfig';
import buildParamsFromInputs from '@/utils/flow/buildParamsFromInputs';
import createGenerationRun from '@/utils/generation/createGenerationRun';
import withRunAttribute from '@/utils/file/withRunAttribute';
import { endOnboardingSession } from '@/utils/environment/onboardingSession';
import { markFreeQuotaSpent } from '@/utils/storage/freeQuotaStorage';

import type { IAppStore } from '@/store';

export interface SubmitResult {
  id: string;
  /** Set only for a multi-output run — see `createGenerationRun`. */
  runId: string | null;
  freeQuotaReason?: FreeQuotaReason;
}

interface SubmitParams {
  flowId: string;
  params?: Record<string, unknown>;
  presetCreditCount?: number | null;
  /** From `resolveFreeQuotaReason`. Absent ⇒ a normal paid run. */
  freeQuotaReason?: FreeQuotaReason;
}

const selector = (store: IAppStore) => ({
  toolId: store.toolId,
  folderName: store.folderName,
  inputs: store.inputs,
  selectedPreset: store.selectedPreset,
});

/**
 * Owns how a submission gets paid for. `freeQuotaReason` runs it against the
 * free-quota webhook first — `onboarding` for a session launched from the
 * onboarding miniapp, `free` for a preset on the remote free list.
 * Once that quota answers 429 the run is re-submitted as a paid one — the
 * first run the user actually pays for, so the first that needs credits.
 * Resolves to `null` when they can't cover it, with the offer screen already
 * open, so callers only have to undo their optimistic placeholder.
 */
const useGenerationSubmit = () => {
  const { toolId, folderName, inputs, selectedPreset } = useAppStore(selector);
  const { requireCredits } = useCredits();

  const submit = useCallback(
    async ({
      flowId,
      params: prebuiltParams,
      presetCreditCount,
      freeQuotaReason,
    }: SubmitParams): Promise<SubmitResult | null> => {
      const params = prebuiltParams ?? buildParamsFromInputs(inputs);
      const presetSlug = selectedPreset?.slug;
      const presetCategory = selectedPreset?.category;

      // Non-null only for a multi-output preset; it is what ties this run's
      // several results together in the creations grid.
      const runId = createGenerationRun({
        outputsCount: selectedPreset?.outputsCount,
        slug: presetSlug,
        timestamp: Date.now(),
      });

      const post = async (reason?: FreeQuotaReason): Promise<SubmitResult> => {
        // Only the onboarding entitlement is reported to the onboarding
        // miniapp — that flag answers "has this user drawn on their onboarding
        // free runs", which a `free` preset run has not.
        if (reason === 'onboarding') markFreeQuotaSpent();

        const { id } = await submitWebHook({
          webhookId: flowId,
          params,
          driveOptions: {
            folderName,
            packageId: toolId,
            // Backend workaround: the backend doesn't accept a separate `slug` parameter — adding an
            // extra parameter causes generations not to be saved into drive — so we smuggle the slug inside the
            // existing `category` field. `run:` rides in the same key for the
            // same reason — see `withRunAttribute`.
            ...(presetSlug && {
              attributes: withRunAttribute({
                attributes: {
                  category: `slug:${presetSlug}, category:${
                    presetCategory || ''
                  }`,
                },
                runId,
              }),
            }),
          },
          notificationConfig: createNotificationConfig(),
          freeQuotaReason: reason,
        });

        refreshMonetization();

        return { id, runId, freeQuotaReason: reason };
      };

      try {
        return await post(freeQuotaReason);
      } catch (error) {
        if (!(error instanceof FreeQuotaExceededError)) throw error;

        // Only the onboarding entitlement is spent by its own 429; a
        // `free` preset quota running out says nothing about the onboarding one,
        // and ending that session here would silently charge the user's next
        // onboarding run.
        if (freeQuotaReason === 'onboarding') endOnboardingSession();

        const hasCredits = await requireCredits({ flowId, presetCreditCount });

        // Whichever free bucket ran out, the fallback is the same: the paid
        // endpoint, once credits are covered.
        return hasCredits ? post() : null;
      }
    },
    [inputs, folderName, toolId, selectedPreset, requireCredits]
  );

  return { submit };
};

export default useGenerationSubmit;
